import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API_BASE } from '../config';
import { useAuth } from './Auth/AuthContext';
import { HiChevronDown, HiCheck, HiXMark } from 'react-icons/hi2';
import RpePainFields from './WorkoutDebrief/RpePainFields';

export default function ExecutionModal({ isOpen, onClose, workout, onConfirm, planSport }) {
    const { t } = useTranslation();
    const { user } = useAuth();
    const stravaOnly = Boolean(user?.strava_athlete_id || user?.strava_athlete_json);
    const isUnplannedSlot = Boolean(workout?.is_unplanned_slot);

    // Strength / cross-training carry no GPS data worth importing: let them be ticked
    // off directly instead of forcing a Strava activity. Keep this aligned with
    // backend/utils/workout_types.js (ADDON_WORKOUT_TYPES).
    const ADDON_WORKOUT_TYPES = ['strength', 'cross_train', 'cross_train_bike'];
    const allowDeclarative = !isUnplannedSlot && ADDON_WORKOUT_TYPES.includes(String(workout?.type || ''));

    const [mode, setMode] = useState(() => {
        // If user is connected to Strava, force Strava mode and hide other tabs.
        if (stravaOnly) return 'strava';
        // Otherwise: last used, fallback to GPX.
        try {
            const saved = localStorage.getItem('runcoach:executionModalMode');
            if (saved === 'strava' || saved === 'gpx' || saved === 'manual') return saved;
        } catch (e) {
            // ignore
        }
        return 'gpx';
    }); // 'strava' | 'gpx' | 'manual'
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [feedback, setFeedback] = useState('');
    // Optional structured feedback (RPE 1-10, pain + location).
    const [feel, setFeel] = useState({ rpe: null, pain: null, pain_location: '' });

    // Strava import state
    const [stravaLoading, setStravaLoading] = useState(false);
    const [stravaError, setStravaError] = useState('');
    const [stravaActivities, setStravaActivities] = useState([]);
    const [selectedActivityId, setSelectedActivityId] = useState(null);

    const getDefaultUnplannedType = () => {
        const s = String(planSport || '').toLowerCase();
        return s === 'bike' ? 'ride_easy' : 'run';
    };

    const [unplannedWorkoutType, setUnplannedWorkoutType] = useState(getDefaultUnplannedType);
    const [declaredDuration, setDeclaredDuration] = useState('');

    // Manual inputs
    const [manualData, setManualData] = useState({
        duration_min: '',
        distance_km: '',
        elevation_gain_m: '',
        avg_hr: ''
    });

    const lastFetchedDateRef = useRef(null);

    // Reset unplanned workout type when opening / switching slot.
    useEffect(() => {
        if (!isOpen) return;
        if (!isUnplannedSlot) return;
        setUnplannedWorkoutType(getDefaultUnplannedType());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, workout?.date, workout?.week_number, workout?.day, isUnplannedSlot]);

    // Opening on a strength session lands on "mark as done" — the whole point is to
    // avoid the Strava round-trip for those.
    useEffect(() => {
        if (!isOpen) return;
        setDeclaredDuration('');
        setFeel({ rpe: null, pain: null, pain_location: '' });
        if (allowDeclarative) setMode('declarative');
        else if (stravaOnly) setMode('strava');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, workout?.id, allowDeclarative, stravaOnly]);

    const loadStravaActivitiesForDate = async () => {
        const date = workout?.date;
        if (!date) {
            setStravaError('Missing workout date (cannot fetch Strava activities).');
            return;
        }

        setStravaLoading(true);
        setStravaError('');
        try {
            const res = await fetch(`${API_BASE}/strava/activities?date=${encodeURIComponent(date)}`, {
                credentials: 'include'
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data?.error || 'Failed to fetch Strava activities');
            setStravaActivities(data.activities || []);
            setSelectedActivityId((data.activities?.[0]?.id) || null);
            lastFetchedDateRef.current = date;
        } catch (e) {
            setStravaError(e?.message || 'Failed to fetch Strava activities');
        } finally {
            setStravaLoading(false);
        }
    };

    // Auto-load activities when the modal opens on the Strava tab.
    useEffect(() => {
        if (!isOpen) return;
        if (mode !== 'strava') return;
        const date = workout?.date;
        if (!date) return;
        if (stravaLoading) return;
        // Fetch once per date while this modal instance is open.
        if (lastFetchedDateRef.current === date && stravaActivities.length > 0) return;
        loadStravaActivitiesForDate();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, mode, workout?.date]);

    // Persist last used mode for better UX.
    useEffect(() => {
        if (stravaOnly) return;
        if (mode === 'declarative') return;
        try {
            localStorage.setItem('runcoach:executionModalMode', mode);
        } catch (e) {
            // ignore
        }
    }, [mode, stravaOnly]);

    // If Strava becomes connected after initial render, force the Strava tab.
    useEffect(() => {
        if (!stravaOnly) return;
        if (allowDeclarative) return;
        if (mode === 'strava') return;
        setMode('strava');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stravaOnly]);

    if (!isOpen) return null;

    const isDeclarative = allowDeclarative && mode === 'declarative';
    const availableModes = [
        ...(allowDeclarative ? [{ key: 'declarative', label: t('workout.mark_done') }] : []),
        ...(!stravaOnly || allowDeclarative ? [{ key: 'strava', label: t('workout.import_strava') || 'Import from Strava' }] : []),
        ...(!stravaOnly ? [{ key: 'gpx', label: t('workout.upload_gpx') }] : []),
        ...(!stravaOnly ? [{ key: 'manual', label: t('workout.manual_entry') }] : [])
    ];

    const handleSubmit = async () => {
        // Feedback is optional: the athlete can add/edit it later from the session page.
        const trimmedFeedback = String(feedback || '').trim();

        setLoading(true);
        const formData = new FormData();

        const effectiveMode = isDeclarative ? 'declarative' : (stravaOnly ? 'strava' : mode);
        formData.append('execution_type', effectiveMode);
        formData.append('week_number', workout.week_number);
        formData.append('day_name', workout.day);
        if (workout?.date) formData.append('date', workout.date);
        // Planned sessions need a stable identifier (supports multiple workouts per day).
        if (!isUnplannedSlot && workout?.id) {
            formData.append('workout_id', String(workout.id));
        }
        formData.append('feedback', trimmedFeedback);
        if (feel.rpe != null) formData.append('rpe', String(feel.rpe));
        if (feel.pain != null) formData.append('pain', feel.pain ? 'true' : 'false');
        if (feel.pain === true && String(feel.pain_location || '').trim()) {
            formData.append('pain_location', String(feel.pain_location).trim());
        }
        if (isUnplannedSlot) {
            formData.append('unplanned_workout_type', String(unplannedWorkoutType || ''));
            formData.append('is_unplanned', 'true');
        }

        if (effectiveMode === 'declarative') {
            const parsed = Number(declaredDuration);
            if (Number.isFinite(parsed) && parsed > 0) formData.append('duration_min', String(parsed));
        } else if (effectiveMode === 'strava') {
            if (!selectedActivityId) {
                setLoading(false);
                return;
            }
            formData.append('activity_id', String(selectedActivityId));
        } else if (effectiveMode === 'gpx') {
            if (!file) return setLoading(false);
            formData.append('gpx', file);
        } else {
            formData.append('manual_data', JSON.stringify(manualData));
        }

        await onConfirm(formData);
        setLoading(false);
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fade-in">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden border border-gray-100 max-h-[90vh] flex flex-col animate-slide-in-bottom md:animate-none">
                <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-white/95 backdrop-blur-md sticky top-0 z-10">
                    <h3 className="text-xl font-black text-gray-900 tracking-tight leading-tight">
                        {t('workout.complete_session')}
                        <span className="block text-sm font-bold text-indigo-500 mt-0.5">{workout.title}</span>
                    </h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100 transition-colors">
                        <HiXMark className="w-6 h-6" />
                    </button>
                </div>

                <div className="p-6 overflow-y-auto">
                    {/* Unplanned session type selector */}
                    {isUnplannedSlot ? (
                        <div className="mb-6 relative">
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">
                                {t('workout.session_type_label')}
                            </label>
                            <div className="relative">
                                <select
                                    className="w-full bg-gray-50 border border-gray-200 rounded-2xl p-4 pr-10 text-gray-900 font-bold focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none appearance-none transition-all cursor-pointer"
                                    value={unplannedWorkoutType}
                                    onChange={(e) => setUnplannedWorkoutType(e.target.value)}
                                >
                                    {[
                                        { value: 'run', label: t('workout.types.run') },
                                        { value: 'long_run', label: t('workout.types.long_run') },
                                        { value: 'workout', label: t('workout.types.workout') },
                                        { value: 'race', label: t('workout.types.race') },
                                        { value: 'cross_train', label: t('workout.types.cross_train') },
                                        { value: 'cross_train_bike', label: t('workout.types.cross_train_bike') },
                                        { value: 'strength', label: t('workout.types.strength') },
                                        { value: 'ride_easy', label: t('workout.types.ride_easy') },
                                        { value: 'long_ride', label: t('workout.types.long_ride') },
                                        { value: 'sweet_spot', label: t('workout.types.sweet_spot') },
                                        { value: 'threshold', label: t('workout.types.threshold') },
                                        { value: 'intervals', label: t('workout.types.intervals') },
                                        { value: 'skills', label: t('workout.types.skills') }
                                    ].map((opt) => (
                                        <option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </select>
                                <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none text-gray-500">
                                    <HiChevronDown className="w-5 h-5" />
                                </div>
                            </div>
                        </div>
                    ) : null}

                    {/* Mode Toggle */}
                    {availableModes.length > 1 ? (
                        <div className="flex bg-gray-100 p-1.5 rounded-xl mb-6">
                            {availableModes.map((m) => (
                                <button
                                    key={m.key}
                                    className={`flex-1 py-2.5 px-1 rounded-lg font-bold text-sm transition-all duration-200 ${mode === m.key ? 'bg-white shadow-sm text-indigo-600 ring-1 ring-black/5' : 'text-gray-500 hover:text-gray-700'}`}
                                    onClick={() => setMode(m.key)}
                                >
                                    {m.label}
                                </button>
                            ))}
                        </div>
                    ) : null}

                    {isDeclarative ? (
                        <div className="space-y-4">
                            <div className="flex items-start gap-3 rounded-2xl border border-indigo-100 bg-indigo-50/60 p-4">
                                <HiCheck className="w-6 h-6 text-indigo-600 shrink-0 mt-0.5" />
                                <div className="text-sm font-medium text-gray-700 leading-relaxed">
                                    {t('workout.declarative_hint')}
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-black text-gray-500 uppercase tracking-widest mb-2">
                                    {t('workout.duration')} (min) — {t('workout.optional')}
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    className="block w-full border border-gray-200 rounded-2xl px-4 py-3 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all outline-none font-bold text-center text-lg tabular-nums"
                                    placeholder={workout?.duration_min ? String(workout.duration_min) : ''}
                                    value={declaredDuration}
                                    onChange={(e) => setDeclaredDuration(e.target.value)}
                                />
                                {workout?.duration_min ? (
                                    <div className="mt-2 text-xs text-gray-400 font-medium text-center">
                                        {t('workout.declarative_duration_default', { minutes: workout.duration_min })}
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    ) : mode === 'strava' ? (
                        <div className="space-y-3">
                            {stravaLoading ? (
                                <div className="flex flex-col items-center justify-center py-8">
                                    <div className="animate-spin rounded-full h-10 w-10 border-4 border-indigo-100 border-t-indigo-600"></div>
                                    <span className="mt-4 text-sm font-bold text-gray-500 animate-pulse">Loading Strava activities…</span>
                                </div>
                            ) : stravaError ? (
                                <div className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-xl p-4 font-medium">
                                    {stravaError}
                                </div>
                            ) : (
                                <>
                                    <div className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">
                                        Activities for <span className="font-mono text-gray-600">{workout?.date}</span>
                                    </div>
                                    {stravaActivities.length === 0 ? (
                                        <div className="text-sm text-gray-600 bg-gray-50 rounded-xl p-4 text-center border border-gray-100">
                                            No activities found for this day.
                                            <div className="mt-3">
                                                <button
                                                    type="button"
                                                    className="text-sm font-bold text-indigo-600 hover:text-indigo-700 hover:underline"
                                                    onClick={loadStravaActivitiesForDate}
                                                >
                                                    Refresh
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="space-y-2">
                                            {stravaActivities.map((a) => (
                                                <button
                                                    key={a.id}
                                                    type="button"
                                                    onClick={() => setSelectedActivityId(a.id)}
                                                    className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 ${
                                                        selectedActivityId === a.id
                                                            ? 'border-indigo-600 bg-indigo-50/50 shadow-sm'
                                                            : 'border-gray-100 hover:border-indigo-200 hover:bg-gray-50'
                                                    }`}
                                                >
                                                    <div className={`font-bold ${selectedActivityId === a.id ? 'text-indigo-900' : 'text-gray-900'}`}>{a.name || `Activity ${a.id}`}</div>
                                                    <div className="text-xs text-gray-500 mt-1 font-medium flex gap-3">
                                                        <span>{(a.distance ? (a.distance / 1000).toFixed(2) : '?')} km</span>
                                                        <span>{a.moving_time ? `${Math.round(a.moving_time / 60)} min` : '?'}</span>
                                                        <span className="font-mono text-gray-400">{String(a.start_date_local || '').replace('T', ' ').slice(0, 16)}</span>
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    ) : mode === 'gpx' ? (
                        <div
                            className={`border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition-all duration-200 ${file ? 'border-green-500 bg-green-50/50' : 'border-gray-200 hover:border-indigo-300 hover:bg-indigo-50/30'}`}
                            onClick={() => document.getElementById('modal-gpx-upload').click()}
                        >
                            <div className="text-5xl mb-4 opacity-80">📂</div>
                            <p className="font-bold text-gray-700">{file ? file.name : t('workout.upload_gpx')}</p>
                            <p className="text-xs text-gray-400 mt-2">Click to select file</p>
                            <input
                                id="modal-gpx-upload"
                                type="file"
                                accept=".gpx"
                                className="hidden"
                                onChange={(e) => setFile(e.target.files[0])}
                            />
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-black text-gray-500 uppercase tracking-widest mb-2 truncate">{t('workout.duration')} (min)</label>
                                <input
                                    type="number"
                                    className="block w-full border border-gray-200 rounded-2xl px-4 py-3 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all outline-none font-bold text-center text-lg tabular-nums"
                                    value={manualData.duration_min}
                                    onChange={(e) => setManualData({...manualData, duration_min: e.target.value})}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-black text-gray-500 uppercase tracking-widest mb-2 truncate">{t('workout.distance')} (km)</label>
                                <input
                                    type="number"
                                    className="block w-full border border-gray-200 rounded-2xl px-4 py-3 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all outline-none font-bold text-center text-lg tabular-nums"
                                    value={manualData.distance_km}
                                    onChange={(e) => setManualData({...manualData, distance_km: e.target.value})}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-black text-gray-500 uppercase tracking-widest mb-2 truncate">{t('workout.elevation')} (m)</label>
                                <input
                                    type="number"
                                    className="block w-full border border-gray-200 rounded-2xl px-4 py-3 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all outline-none font-bold text-center text-lg tabular-nums"
                                    value={manualData.elevation_gain_m}
                                    onChange={(e) => setManualData({...manualData, elevation_gain_m: e.target.value})}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-black text-gray-500 uppercase tracking-widest mb-2 truncate">{t('workout.avg_hr')} (bpm)</label>
                                <input
                                    type="number"
                                    className="block w-full border border-gray-200 rounded-2xl px-4 py-3 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all outline-none font-bold text-center text-lg tabular-nums"
                                    value={manualData.avg_hr}
                                    onChange={(e) => setManualData({...manualData, avg_hr: e.target.value})}
                                />
                            </div>
                        </div>
                    )}

                    {/* Feedback Area (optional) */}
                    <div className="mt-8 border-t border-gray-100 pt-6">
                        <label className="block text-xs font-black text-gray-500 uppercase tracking-widest mb-3">
                            {t('workout.feedback_label')}
                            <span className="ml-2 normal-case tracking-normal text-gray-400 font-bold">({t('workout.optional')})</span>
                        </label>
                        <textarea
                            className="w-full border border-gray-200 p-4 rounded-2xl text-base font-medium focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all resize-none bg-gray-50 focus:bg-white placeholder-gray-400 leading-relaxed"
                            rows="4"
                            placeholder={t('workout.user_feedback_placeholder')}
                            value={feedback}
                            onChange={(e) => setFeedback(e.target.value)}
                        />
                        {!isDeclarative ? (
                            <div className="mt-2 text-xs text-gray-400 font-medium">
                                {t('feedback.optional_hint')}
                            </div>
                        ) : null}
                        <div className="mt-5">
                            <RpePainFields value={feel} onChange={setFeel} disabled={loading} />
                        </div>
                    </div>
                </div>

                <div className="p-6 border-t border-gray-100 bg-gray-50/50 flex justify-end gap-3 sticky bottom-0 z-10">
                    <button onClick={onClose} className="px-5 py-2.5 text-gray-600 font-bold hover:bg-gray-200 rounded-full transition-colors text-sm uppercase tracking-wide">{t('workout.cancel')}</button>
                    <button
                        onClick={handleSubmit}
                        disabled={
                            loading ||
                            (!isDeclarative && (
                                (mode === 'strava' && !selectedActivityId) ||
                                (!stravaOnly && mode === 'gpx' && !file) ||
                                (!stravaOnly && mode === 'manual' && !manualData.duration_min)
                            ))
                        }
                        className={`px-6 py-2.5 bg-indigo-600 text-white rounded-full font-bold shadow-lg transition-all duration-200 text-sm uppercase tracking-wide ${
                            loading ? 'opacity-70 cursor-wait' :
                            (!isDeclarative && ((mode === 'strava' && !selectedActivityId) || (!stravaOnly && mode === 'gpx' && !file)))
                            ? 'bg-gray-300 cursor-not-allowed shadow-none text-gray-500'
                            : 'hover:bg-indigo-700 hover:shadow-indigo-500/40 hover:-translate-y-0.5'
                        }`}
                    >
                        {loading
                            ? (isDeclarative ? t('workout.saving') : t('workout.analyzing'))
                            : isDeclarative
                                ? t('workout.mark_done')
                                : (!stravaOnly && mode === 'manual' ? t('workout.save_manual') : t('workout.complete_session'))}
                    </button>
                </div>
            </div>
        </div>
    );
}

